import moment from 'moment';

import schema from './validate';
import api, { optimiseApi } from '../api';
import { DATE_FORMAT } from '../../utils/query';

export const getRoutes = async (depotId, sectionId, date, userId, polling) => {
  const { data } = await api.get(
    `/depot/${depotId}/section/${sectionId || ''}/route`,
    {
      date: moment.utc(date, DATE_FORMAT).toDate().getTime(),
      userId,
      ...(polling ? { polling: true } : {}),
    }
  );

  try {
    const result = await schema.validateAsync(data);

    return {
      routes: result.routes.sort((a, b) =>
        a.routeCode.localeCompare(b.routeCode)
      ),
      depotStats: result.depotStats,
    };
  } catch (error) {
    // eslint-disable-next-line
    console.warn('Invalid getRoutes data', error);
  }
};

export const getRoutesTransfer = async (depotId, date, userId) => {
  const { data } = await api.get(`/depot/${depotId}/route`, {
    date: moment.utc(date, DATE_FORMAT).toDate().getTime(),
    userId,
  });
  return data;
};

export const getRouteDetail = async (
  depotCode,
  routeId,
  routePlannerDowngrades
) => {
  const { data } = await api.get(`/depot/${depotCode}/route/${routeId}`, {
    'route-planner-downgrades': routePlannerDowngrades,
  });

  return data;
};

export const resyncRouteDetail = async (
  depotCode,
  routeId,
  routePlannerDowngrades
) => {
  const { data } = await api.put(
    `/depot/${depotCode}/route/${routeId}/resync`,
    {
      'route-planner-downgrades': routePlannerDowngrades,
    }
  );

  return data;
};

export const unlockRoute = async (
  depotCode,
  userName,
  { routeId, checksum },
  forceUnlock
) =>
  api.post(`/depot/${depotCode}/route/unlock`, null, null, {
    userName,
    route: [{ routeId, checksum, forceUnlock }],
  });

export const setCutOffTime = async (
  depotCode,
  date,
  { waveCutOff, departTime }
) =>
  api.put(`/depot/${depotCode}/routes`, null, null, {
    waveCutOff,
    departTime,
    date: `${moment.utc(date, DATE_FORMAT).toDate().getTime()}`,
  });

export const setDisasterRecoveryMode = async (depotCode) =>
  api.put(`/depot/${depotCode}/mode/disaster-recovery`);

export const lockRoute = async (
  depotCode,
  userName,
  { routeId, checksum },
  stops = []
) =>
  api.post(`/depot/${depotCode}/route/lock`, null, null, {
    userName,
    stops,
    route: [{ routeId, checksum }],
  });

export const changeRouteStatus = async (depotCode, routeStatus, routeId) =>
  api.post(`/depot/${depotCode}/route/${routeId}/status`, null, null, {
    routeStatus,
  });

export const scheduleRoute = async (
  depotCode,
  routeId,
  scheduleData,
  abortController
) =>
  optimiseApi.post(
    `/depot/${depotCode}/route/${routeId}/schedule`,
    null,
    null,
    scheduleData,
    { signal: abortController?.signal }
  );

export const saveRoute = async (depotCode, routeId, routeData) =>
  optimiseApi.put(
    `/depot/${depotCode}/route/${routeId}`,
    null,
    null,
    routeData
  );

export const createRoute = async (depotCode, route) =>
  optimiseApi.post(`/depot/${depotCode}/route/`, null, null, route);

export const validateRouteCode = async (depotCode, routeCode, date) => {
  const { data = {} } = await optimiseApi.get(
    `/depot/${depotCode}/route/${routeCode}/validate`,
    {
      date: moment.utc(date, DATE_FORMAT).format('YYYY-MM-DD'),
    }
  );

  return data;
};

export const getWaveChoiceRouteId = async (depotCode, routeId) => {
  const { data } = await api.get(
    `/depot/${depotCode}/route/${routeId}/choice-route`
  );
  return data;
};

export const getPartiallyCaddedStops = async (depotCode, routeId) => {
  const { data } = await api.get(
    `/depot/${depotCode}/route/${routeId}/partially-cadded-stops`
  );
  return data;
};

export const closeTote = async (depotCode, routeCode) =>
  api.put(`/depot/${depotCode}/route/${routeCode}/close-tote`);

export const getDriverLocationForDepartedRoute = async ([
  depotCode,
  routeId,
  limit,
]) => {
  const { data } = await api.get(
    `/depot/${depotCode}/route/${routeId}/driver-location`,
    (limit && { limit }) || {}
  );
  return data;
};
